import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
  TextField, MenuItem, Table, TableHead, TableBody, TableRow, TableCell, Typography,
} from "@mui/material";
import { formatHistoryValue, HistoryValue } from "../ReceiptDetails/ReceiptEditHistory";

const previewFields = { customer: "Customer", vehicle: "Vehicle", date: "Date", miles: "Odometer", receiptType: "Document type",
  services: "Services and parts", tires: "Tires", techNotes: "Technician notes",
  oil: "Oil", oilQuantity: "Oil quantity", oilPrice: "Oil price", extraOilSelection: "Extra oil",
  extraOilQuantity: "Extra oil quantity", extraOilPrice: "Extra oil price", oilFilter: "Oil filter",
  oilFilterQuantity: "Filter quantity", oilFilterPrice: "Filter price", fullServiceIncluded: "Full service",
  fullServicePrice: "Full service price", tax: "Tax", discountAmount: "Discount", totalPrice: "Total",
  taxType: "Tax rate", taxIncluded: "Tax enabled", discountIncluded: "Discount enabled", discountType: "Discount type",
  discount: "Discount", checkInReasons: "Check-in reasons", checkInOther: "Other check-in reason",
};

const comparableDetails = (value) => {
  if (Array.isArray(value)) return value.map(comparableDetails);
  if (value && typeof value === "object") {
    if (value.value && typeof value.value === "object") return comparableDetails(value.value);
    return Object.fromEntries(Object.keys(value).sort().filter((key) =>
      !["lineItemId", "linkedPartId", "createdAt", "updatedAt", "available", "purchaseHistory", "user_id", "shop_id", "__v"].includes(key))
      .map((key) => [key, comparableDetails(value[key])]));
  }
  return value ?? null;
};

function ReceiptEditReviewDialog({ open, original = {}, next = {}, loading, error, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [disposition, setDisposition] = useState("");
  const [validationError, setValidationError] = useState("");
  useEffect(() => {
    if (open) {
      setReason("");
      setDisposition(original.overpaymentDisposition || "");
      setValidationError("");
    }
  }, [open, original.overpaymentDisposition]);
  const paid = Number(original.paid || 0);
  const total = Number(next.totalPrice || 0);
  const balance = Math.max(0, total - paid);
  const overpayment = Math.max(0, paid - total);
  const changes = Object.entries(previewFields).map(([field, label]) => ({ field, label,
    before: formatHistoryValue(field, original[field]), after: formatHistoryValue(field, next[field]),
  })).filter((change) => change.before !== change.after ||
    (original[change.field] && typeof original[change.field] === "object" &&
      JSON.stringify(comparableDetails(original[change.field])) !== JSON.stringify(comparableDetails(next[change.field]))));
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Review Receipt Changes</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>Existing payments will stay unchanged. Saving these edits does not collect another payment.</Alert>
        <Typography sx={{ mb: 2 }}>Already paid: ${paid.toFixed(2)} · New total: ${total.toFixed(2)} · Balance due: ${balance.toFixed(2)}</Typography>
        {changes.length ? <Table size="small" aria-label="Review changes">
          <TableHead><TableRow><TableCell>Changed</TableCell><TableCell>Before</TableCell><TableCell>After</TableCell></TableRow></TableHead>
          <TableBody>{changes.map((change) => <TableRow key={change.field}>
            <TableCell>{change.label}</TableCell><TableCell sx={{ whiteSpace: "pre-wrap" }}><HistoryValue field={change.field} value={original[change.field]} /></TableCell>
            <TableCell sx={{ whiteSpace: "pre-wrap" }}><HistoryValue field={change.field} value={next[change.field]} /></TableCell>
          </TableRow>)}</TableBody>
        </Table> : <Typography color="text.secondary">Review your receipt before saving.</Typography>}
        {overpayment > 0 && <>
          <Alert severity="warning" sx={{ my: 2 }}>This edit leaves an overpayment of ${overpayment.toFixed(2)}.</Alert>
          <TextField select fullWidth label="Overpayment handling" value={disposition} onChange={(event) => setDisposition(event.target.value)}
            helperText="Record how the overpayment should be handled. This does not issue a refund or apply the credit to another invoice.">
            <MenuItem value="customer_credit">Customer credit</MenuItem><MenuItem value="refund_due">Refund due</MenuItem>
          </TextField>
        </>}
        <TextField fullWidth label="Reason for edit" multiline minRows={2} value={reason}
          onChange={(event) => setReason(event.target.value)} inputProps={{ maxLength: 500 }} sx={{ mt: 3 }}
          helperText="This reason and your changes will be recorded in edit history." />
        {(validationError || error) && <Alert severity="error" sx={{ mt: 2 }}>{validationError || error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Back to editing</Button>
        <Button variant="contained" disabled={loading} onClick={() => {
          if (!reason.trim()) { setValidationError("Enter a reason for this edit."); return; }
          if (overpayment > 0 && !disposition) { setValidationError("Choose how to handle the overpayment."); return; }
          setValidationError("");
          onConfirm({ editReason: reason.trim(), overpaymentDisposition: disposition });
        }}>{loading ? "Saving..." : "Confirm Changes"}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReceiptEditReviewDialog;
